import { abs, float, mix, mx_noise_float, positionLocal, smoothstep, vec3 } from 'three/tsl';
import * as THREE from 'three/webgpu';
import { bakeMeshes } from '../../engine/render/bakeMeshes';
import type { Weapon } from './weapons';

type N = any;

/**
 * The weapons' models, built from code: each grips at its origin, points along +Y (blade, head
 * or point), with its edges (a hammer's striking face: +X) across ±X and its flats facing ±Z.
 * The same models are held in first person (WeaponView) and by the hero in third person.
 */

export interface WeaponMaterials {
  steel: THREE.MeshStandardNodeMaterial;
  iron: THREE.MeshStandardNodeMaterial;
  brass: THREE.MeshStandardNodeMaterial;
  wood: THREE.MeshStandardNodeMaterial;
  leather: THREE.MeshStandardNodeMaterial;
}

/**
 * Where a weapon is held, along its +Y (m from its origin): the main hand at 0, and the other
 * hand here (null: one-handed). A greatsword's and a maul's hands sit together, a spear's apart.
 */
export function offHand(w: Weapon): number | null {
  switch (w.id) {
    case 'greatsword':
      return -0.12;
    case 'maul':
      return -0.34;
    case 'spear':
      return 0.42;
    default:
      return null;
  }
}

/** Metal, wood and leather with a little of their grain close up (in the model's own frame). */
export function weaponMaterials(): WeaponMaterials {
  const p: N = positionLocal;
  // Steel: brushed along the blade, with fine scratches catching the light.
  const steel = new THREE.MeshStandardNodeMaterial({ metalness: 0.6 });
  const brushed = mx_noise_float(p.mul(vec3(90, 3, 90)));
  const scratches = smoothstep(0.55, 0.75, abs(mx_noise_float(p.mul(vec3(160, 22, 160)))));
  steel.colorNode = mix(vec3(0.6, 0.62, 0.65), vec3(0.72, 0.74, 0.77), brushed.mul(0.5).add(0.5));
  steel.roughnessNode = float(0.3).add(brushed.mul(0.05)).sub(scratches.mul(0.12));
  // Dark iron: forged, a little pitted.
  const iron = new THREE.MeshStandardNodeMaterial({ metalness: 0.55 });
  const pits = mx_noise_float(p.mul(45));
  iron.colorNode = vec3(0.26, 0.255, 0.25).mul(pits.mul(0.18).add(1));
  iron.roughnessNode = float(0.5).add(pits.mul(0.12));
  const brass = new THREE.MeshStandardNodeMaterial({ metalness: 0.65, roughness: 0.38 });
  brass.colorNode = vec3(0.55, 0.4, 0.18).mul(mx_noise_float(p.mul(60)).mul(0.1).add(1));
  // Wood: grain running along the shaft, in rings of lighter and darker growth.
  const wood = new THREE.MeshStandardNodeMaterial({ metalness: 0 });
  const grain = mx_noise_float(p.mul(vec3(70, 2.5, 70)));
  const rings = smoothstep(-0.2, 0.6, mx_noise_float(p.mul(vec3(160, 9, 160))));
  wood.colorNode = mix(
    vec3(0.2, 0.115, 0.06),
    vec3(0.36, 0.22, 0.12),
    grain.mul(0.35).add(0.5).add(rings.mul(0.2)),
  );
  wood.roughnessNode = float(0.72).add(grain.mul(0.06));
  // Leather wraps: dark, worn lighter in places.
  const leather = new THREE.MeshStandardNodeMaterial({ metalness: 0 });
  const wear = mx_noise_float(p.mul(38));
  leather.colorNode = mix(vec3(0.11, 0.065, 0.035), vec3(0.2, 0.12, 0.07), smoothstep(-0.3, 0.6, wear));
  leather.roughnessNode = float(0.78).sub(wear.mul(0.08));
  return { steel, iron, brass, wood, leather };
}

/**
 * A blade from y0 to its point at y1: a diamond cross-section (a ridge down each flat, sharp
 * edges), `width` at its base narrowing to the point, `thick` at the ridge.
 */
function blade(y0: number, y1: number, width: number, thick: number, taper = 0.6): THREE.BufferGeometry {
  const stations = 7;
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i < stations; i++) {
    const t = i / (stations - 1);
    const y = y0 + (y1 - y0) * t * 0.88;
    // Narrowing gently, then quickly into the point.
    const w = (width / 2) * (1 - (1 - taper) * t) * (t > 0.8 ? 1 - (t - 0.8) * 2.6 : 1);
    const th = (thick / 2) * (1 - 0.4 * t);
    pos.push(w, y, 0, 0, y, th, -w, y, 0, 0, y, -th);
  }
  const tip = pos.length / 3;
  pos.push(0, y1, 0);
  for (let i = 0; i < stations - 1; i++) {
    for (let k = 0; k < 4; k++) {
      const a = i * 4 + k;
      const b = i * 4 + ((k + 1) % 4);
      const c = a + 4;
      const d = b + 4;
      idx.push(a, c, b, b, c, d);
    }
  }
  const last = (stations - 1) * 4;
  for (let k = 0; k < 4; k++) idx.push(last + k, tip, last + ((k + 1) % 4));
  // The base, closed.
  idx.push(0, 1, 2, 0, 2, 3);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  const flat = g.toNonIndexed();
  flat.computeVertexNormals();
  return flat;
}

/** A grip wrapped in leather: a column whose radius rises and falls with each turn of the wrap. */
function wrappedGrip(y0: number, y1: number, r: number, turns: number): THREE.BufferGeometry {
  const pts: THREE.Vector2[] = [];
  const steps = Math.max(8, turns * 4);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    pts.push(new THREE.Vector2(r * (1 + 0.12 * Math.abs(Math.sin(t * turns * Math.PI))), y0 + (y1 - y0) * t));
  }
  return new THREE.LatheGeometry(pts, 12);
}

/** A mesh of `geo` in `mat`, placed and turned. */
function part(
  group: THREE.Group,
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  y = 0,
  rotate?: (g: THREE.BufferGeometry) => void,
): THREE.Mesh {
  rotate?.(geo);
  const m = new THREE.Mesh(geo, mat);
  m.position.y = y;
  m.castShadow = false;
  m.frustumCulled = false;
  group.add(m);
  return m;
}

/** The model of `w` (see the module's comment for its frame). */
export function buildWeaponModel(w: Weapon, mats: WeaponMaterials): THREE.Group {
  const g = new THREE.Group();
  g.name = `weapon ${w.id}`;
  if (w.kind === 'sword') {
    const great = w.id === 'greatsword';
    const len = great ? 1.12 : 0.78;
    const gripTop = great ? 0.16 : 0.08;
    const gripLow = great ? -0.17 : -0.08;
    part(g, wrappedGrip(gripLow, gripTop, great ? 0.017 : 0.016, great ? 9 : 5), mats.leather);
    // The crossguard, with rounded ends.
    const guardW = great ? 0.3 : 0.2;
    part(g, new THREE.BoxGeometry(guardW, 0.024, 0.032), mats.brass, gripTop + 0.012);
    for (const s of [-1, 1]) {
      const end = part(g, new THREE.SphereGeometry(0.02, 10, 8), mats.brass, gripTop + 0.014);
      end.position.x = (s * guardW) / 2;
    }
    // A wheel pommel.
    part(g, new THREE.CylinderGeometry(0.03, 0.03, 0.02, 14), mats.brass, gripLow - 0.016, (geo) =>
      geo.rotateZ(Math.PI / 2),
    );
    part(
      g,
      blade(gripTop + 0.024, gripTop + 0.024 + len, great ? 0.062 : 0.05, great ? 0.012 : 0.01),
      mats.steel,
    );
    // A ricasso collar where the blade meets the guard.
    part(g, new THREE.BoxGeometry(great ? 0.07 : 0.056, 0.03, 0.016), mats.steel, gripTop + 0.04);
  } else if (w.kind === 'hammer') {
    const maul = w.id === 'maul';
    const shaftLow = maul ? -0.5 : -0.24;
    const shaftTop = maul ? 0.84 : 0.56;
    const r = maul ? 0.024 : 0.018;
    part(
      g,
      new THREE.CylinderGeometry(r * 0.9, r, shaftTop - shaftLow, 10),
      mats.wood,
      (shaftLow + shaftTop) / 2,
    );
    // Leather where the hands go, and an iron butt cap.
    part(g, wrappedGrip(maul ? -0.42 : -0.1, maul ? 0.08 : 0.1, r * 1.12, maul ? 10 : 4), mats.leather);
    part(g, new THREE.CylinderGeometry(r * 1.15, r * 1.25, 0.03, 10), mats.iron, shaftLow + 0.01);
    const headY = shaftTop - (maul ? 0.06 : 0.04);
    if (maul) {
      // A heavy octagonal head with two flat faces and iron bands.
      part(g, new THREE.CylinderGeometry(0.085, 0.085, 0.28, 8), mats.iron, headY, (geo) =>
        geo.rotateZ(Math.PI / 2),
      );
      for (const s of [-1, 1]) {
        const band = part(g, new THREE.CylinderGeometry(0.092, 0.092, 0.025, 8), mats.steel, headY, (geo) =>
          geo.rotateZ(Math.PI / 2),
        );
        band.position.x = s * 0.1;
        const face = part(g, new THREE.CylinderGeometry(0.075, 0.08, 0.02, 8), mats.steel, headY, (geo) =>
          geo.rotateZ(Math.PI / 2),
        );
        face.position.x = s * 0.145;
      }
      // Langets: iron straps down the shaft from the head.
      part(g, new THREE.BoxGeometry(0.008, 0.2, 0.05), mats.iron, headY - 0.13);
    } else {
      // A war hammer: a square striking face (+X) and a curved beak behind it (−X).
      part(g, new THREE.BoxGeometry(0.11, 0.07, 0.07), mats.iron, headY);
      const face = part(g, new THREE.BoxGeometry(0.03, 0.085, 0.085), mats.steel, headY);
      face.position.x = 0.065;
      const beak = part(g, new THREE.ConeGeometry(0.028, 0.16, 6), mats.steel, headY, (geo) => {
        geo.rotateZ(Math.PI / 2 + 0.25);
      });
      beak.position.x = -0.12;
      // A short spike on top.
      part(g, new THREE.ConeGeometry(0.018, 0.08, 6), mats.steel, headY + 0.07);
      part(g, new THREE.BoxGeometry(0.006, 0.14, 0.04), mats.iron, headY - 0.09);
    }
  } else {
    // Spear: an ash shaft, a socketed, leaf-shaped head, bindings and a butt spike.
    const low = -0.95;
    const top = 1.02;
    part(g, new THREE.CylinderGeometry(0.0155, 0.018, top - low, 10), mats.wood, (low + top) / 2);
    part(g, new THREE.CylinderGeometry(0.019, 0.022, 0.09, 10), mats.iron, top + 0.035);
    part(g, blade(top + 0.08, top + 0.4, 0.07, 0.016, 0.8), mats.steel);
    for (const y of [top - 0.02, 0.42, 0.0])
      part(g, wrappedGrip(y - 0.04, y + 0.04, 0.0185, 3), mats.leather);
    part(g, new THREE.ConeGeometry(0.018, 0.08, 8), mats.iron, low - 0.03, (geo) => geo.rotateX(Math.PI));
  }
  // One mesh per material: a weapon draws in a few calls.
  bakeMeshes(g);
  return g;
}
